import React from 'react';
import Select from 'react-select';
import { useDropzone } from 'react-dropzone';
import findIndex from 'lodash/findIndex';
import { withTranslation, WithTranslation } from 'react-i18next';
import { PICTURES } from '../../constants/config';
import { TargetNode } from '../../models/TargetNode';

interface PictureOption {
  value: number;
  // May be undefined: `pictures` spreads in node.picture, which is unset on a
  // target that has never had one chosen. Typed honestly rather than asserted —
  // that entry reaches <img src={…}> as-is today.
  label?: string;
}

interface PictureSelectorOwnProps {
  node: TargetNode;
  className?: string;
  onChangePicture: (option: any) => void;
  onFileUpload: (files: File[]) => void;
}

const PictureSelector = ({
  node, className, onChangePicture, onFileUpload, t,
}: PictureSelectorOwnProps & WithTranslation) => {
  // Default images + this target's custom uploads + the currently-selected
  // picture, de-duplicated (was Immutable List.push(...).toSet().toJS()).
  const customPictures = node.customizations?.picture || [];
  const pictures: Array<string | undefined> = (PICTURES as Array<string | undefined>).concat(
    Array.from(new Set([...customPictures, node.picture]))
  );

  // NOTE: `|| 0` never fires as intended — findIndex returns -1 when not found,
  // and -1 is truthy, so a miss would yield -1 rather than 0 and index past the
  // array. It holds today only because `pictures` always contains node.picture
  // (it is spread in above, undefined included). Left as-is; changing it is a
  // behaviour decision, not a typing one.
  const currentPicture = findIndex(pictures, picture => picture === node.picture) || 0;

  const pictureSources: PictureOption[] = pictures.map((picture, i) => ({
    value: i,
    label: picture,
  }));

  // Menu image options: each row renders the image itself rather than its URL.
  const optionsRenderer = (option: any, innerProps: any) => (
    <div className="image--preview" {...innerProps}>
      <img src={option} alt="Target image" />
    </div>
  );

  // The currently-selected default image, shown ABOVE the dropdown. Suppressed
  // when a custom upload is previewing, since that is rendered instead.
  const currentPictureRenderer = (option?: PictureOption) => {
    if (node.customImagePreview || !option) {
      return null;
    }
    return (
      <div className="image--preview">
        <img src={option.label} alt="Target image" />
      </div>
    );
  };

  const renderImageUpload = () => {
    if (!node.customImagePreview) {
      return null;
    }
    return (
      <div className="image--preview">
        <img src={node.customImagePreview} alt="Uploaded target image" />
      </div>
    );
  };

  // react-dropzone v15 hook API (replaces the v3 <Dropzone> children component).
  // accept is now an object map (mime -> extensions); onDrop's first arg is the
  // accepted files array, matching the existing onFileUpload(files) handler.
  const { getRootProps, getInputProps } = useDropzone({
    multiple: false,
    accept: { 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'] },
    onDrop: onFileUpload,
  });

  return (
    <fieldset className={className}>
      {currentPictureRenderer(pictureSources[currentPicture])}
      <legend>{t('target.editor.label.picture')}</legend>
      <Select
        className={'image-selector'}
        classNamePrefix={'react-select'}
        // Deliberately kept verbatim: this passes the bare INDEX where
        // react-select wants an option object, so nothing matches and the
        // dropdown shows its placeholder rather than the current picture. That
        // is why the selected image is rendered separately above. Passing the
        // real option would change what the closed dropdown displays (to a long
        // image URL), so it is a visual decision rather than a typing fix.
        value={currentPicture as any}
        options={pictureSources}
        components={{
          Option: ({ children, innerProps }: any) => optionsRenderer(children, innerProps)
        }}
        onChange={onChangePicture} />
      <div {...getRootProps({ className: 'dropzone' })}>
        <input {...getInputProps()} />
        <button className="button success" type="button">
          <i className="fa fa-picture-o" aria-hidden="true"></i>
          {t('target.editor.button.picture')}
        </button>
      </div>
      {renderImageUpload()}
    </fieldset>
  );
};

export default React.memo(withTranslation('common')(PictureSelector));
